import React from "react";
import { Icon } from "../UIElements/Icon";
import { useGlobalUtils } from "../../../lib/globalUtils";

const LoyaltyCard = ({ loyaltyLevel }) => {
  const { parseDate } = useGlobalUtils();

  const getLoyaltyIcon = (level) => {
    switch (level) {
      case 1:
        return "Star";
      case 2:
        return "Award";
      case 3:
        return "Crown";
      default:
        return "Star";
    }
  };

  const getLoyaltyColor = (level) => {
    switch (level) {
      case 1:
        return "text-primary";
      case 2:
        return "text-primary";
      case 3:
        return "text-warning";
      default:
        return "text-primary";
    }
  };

  return (
    <div className="border-border bg-card rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-muted rounded-lg p-3">
            <Icon
              name={getLoyaltyIcon(loyaltyLevel.id)}
              className={`h-6 w-6 ${getLoyaltyColor(loyaltyLevel.id)}`}
            />
          </div>
          <div>
            <h4 className="text-foreground text-lg font-semibold">
              {loyaltyLevel.name}
            </h4>
            <p className="text-muted-foreground text-sm">Loyalty Level</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Conditional Layout: Two columns with image, single column without */}
        {loyaltyLevel.image ? (
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
            {/* Image Section */}
            <div className="text-center md:text-left">
              <div className="relative inline-block">
                <div
                  className={`absolute -inset-1 rounded-2xl ${getLoyaltyColor(loyaltyLevel.id).replace("text-", "bg-").replace("-400", "-500")} opacity-75 blur-sm`}
                ></div>
                <div className="border-border relative rounded-2xl border bg-white p-4">
                  <img
                    src={loyaltyLevel.image}
                    alt={loyaltyLevel.name}
                    className="mx-auto h-45 w-45 rounded-xl object-cover shadow-lg md:mx-0"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="mx-auto flex hidden h-45 w-45 items-center justify-center rounded-xl md:mx-0">
                    <Icon
                      name={getLoyaltyIcon(loyaltyLevel.id)}
                      className={`h-16 w-16 ${getLoyaltyColor(loyaltyLevel.id)}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-4">
              {/* Points Requirements */}
              <div className="text-center md:text-left">
                <p className="text-muted-foreground mb-1 text-sm">
                  Minimum Points Required
                </p>
                <p
                  className={`text-3xl font-bold ${getLoyaltyColor(loyaltyLevel.id)}`}
                >
                  {loyaltyLevel.minimum_points.toLocaleString()} pts
                </p>
              </div>

              {/* Loyalty Details */}
              <div className="space-y-3">
                {loyaltyLevel.description && (
                  <div className="bg-muted/50 mb-3 rounded-lg p-3">
                    <p className="text-foreground text-sm">
                      <span className="text-foreground font-medium">
                        Description:
                      </span>{" "}
                      {loyaltyLevel.description}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Accumulation Rate:
                  </span>
                  <span className="text-foreground text-sm font-medium">
                    {loyaltyLevel.accumulation_rate}
                  </span>
                </div>

                {loyaltyLevel.benefits?.length > 0 && (
                  <div className="flex items-start">
                    <span className="text-muted-foreground min-w-[80px] text-sm">
                      Benefits:
                    </span>
                    <ul className="text-foreground ml-4 max-w-xs list-inside list-disc space-y-1 text-right text-sm">
                      {loyaltyLevel.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {loyaltyLevel.maximum_accumulation_percentage && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      Max Accumulation:
                    </span>
                    <span className="text-foreground text-sm">
                      {loyaltyLevel.maximum_accumulation_percentage}%
                    </span>
                  </div>
                )}

                {loyaltyLevel.maximum_accumulation_fixed && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      Max Fixed Points:
                    </span>
                    <span className="text-foreground text-sm">
                      {loyaltyLevel.maximum_accumulation_fixed.toLocaleString()}{" "}
                      pts
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Created:
                  </span>
                  <span className="text-foreground text-sm">
                    {parseDate(loyaltyLevel.created_at)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Last Updated:
                  </span>
                  <span className="text-foreground text-sm">
                    {parseDate(loyaltyLevel.updated_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Points Requirements */}
            <div className="text-center">
              <p className="text-muted-foreground mb-1 text-sm">
                Minimum Points Required
              </p>
              <p
                className={`text-3xl font-bold ${getLoyaltyColor(loyaltyLevel.id)}`}
              >
                {loyaltyLevel.minimum_points.toLocaleString()} pts
              </p>
            </div>

            {/* Loyalty Details */}
            <div className="space-y-3">
              {loyaltyLevel.description && (
                <div className="bg-muted/50 mb-3 rounded-lg p-3">
                  <p className="text-foreground text-sm">
                    <span className="text-foreground font-medium">
                      Description:
                    </span>{" "}
                    {loyaltyLevel.description}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  Accumulation Rate:
                </span>
                <span className="text-foreground text-sm font-medium">
                  {loyaltyLevel.accumulation_rate}
                </span>
              </div>

              {loyaltyLevel.benefits?.length > 0 && (
                <div className="flex items-start">
                  <span className="text-muted-foreground min-w-[80px] text-sm">
                    Benefits:
                  </span>
                  <ul className="text-foreground ml-4 max-w-xs list-inside list-disc space-y-1 text-right text-sm">
                    {loyaltyLevel.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              {loyaltyLevel.maximum_accumulation_percentage && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Max Accumulation:
                  </span>
                  <span className="text-foreground text-sm">
                    {loyaltyLevel.maximum_accumulation_percentage}%
                  </span>
                </div>
              )}

              {loyaltyLevel.maximum_accumulation_fixed && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Max Fixed Points:
                  </span>
                  <span className="text-foreground text-sm">
                    {loyaltyLevel.maximum_accumulation_fixed.toLocaleString()}{" "}
                    pts
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">Created:</span>
                <span className="text-foreground text-sm">
                  {parseDate(loyaltyLevel.created_at)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  Last Updated:
                </span>
                <span className="text-foreground text-sm">
                  {parseDate(loyaltyLevel.updated_at)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyCard;
